import {
    confirmedExcludedSkills,
    OrganizationGroupError,
    organizationGroupQueryKeys,
    organizationGroupErrorMessage,
    SkillExclusionRemovalUnconfirmedError,
    useExcludeOrganizationGroupSkills,
    useIsOrganizationGroupSkillExclusionPending,
    useOrganizationGroup,
    useOrganizationGroupExcludableSkills,
    useRemoveOrganizationGroupSkillExclusion,
    useRetryOrganizationGroupSkillExclusionRefresh,
    type IOrganizationGroupDetail,
    type IOrganizationGroupSkillMinimal,
    type OrganizationGroupSkillExclusionConfirmation,
} from "@/api/organization-groups";
import { Button } from "@/components/ui/button";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { hashKey, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import {
    useEffect,
    useRef,
    useState,
    useSyncExternalStore,
    type ReactNode,
} from "react";
import { toast } from "sonner";
import { ExcludedSkillsCombobox } from "./ExcludedSkillsCombobox";

type RemovalAlert =
    | {
          kind: "unconfirmed" | "concurrent";
          skill: IOrganizationGroupSkillMinimal;
          readCount: number;
      }
    | {
          kind: "failed";
          skill: IOrganizationGroupSkillMinimal;
          readCount: number;
          message: string;
      };

interface DetailRead {
    count: number;
    group: IOrganizationGroupDetail | undefined;
}

type FocusRequest =
    | { kind: "excluded"; skillIds: number[] }
    | { kind: "removed"; skillId: number; index: number }
    | { kind: "restore"; skillId?: number };

const joinNames = (names: string[]) =>
    names.length > 1
        ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`
        : (names[0] ?? "");

const refusalMessage = (
    refused: IOrganizationGroupSkillMinimal[],
    remaining: number
) =>
    `${joinNames(refused.map((skill) => skill.name))} can no longer be excluded, so ${
        refused.length === 1 ? "it was" : "they were"
    } removed from the selection.${
        remaining > 0 ? " The other selections are still ready to exclude." : ""
    }`;

const unconfirmedExclusionMessage = (
    skills: IOrganizationGroupSkillMinimal[]
) =>
    `${joinNames(skills.map((skill) => skill.name))} could not be confirmed as excluded. Review the group's current exclusions and try again.`;

const removalAlertMessage = (alert: RemovalAlert) => {
    switch (alert.kind) {
        case "unconfirmed":
            return `Removing ${alert.skill.name} could not be confirmed. Retry refresh to check the group's current exclusions.`;
        case "concurrent":
            return `${alert.skill.name} is still excluded. The group changed while it was being removed.`;
        case "failed":
            return alert.message;
    }
};

const isFocusable = (
    element: HTMLElement | null | undefined
): element is HTMLElement =>
    !!element &&
    element.isConnected &&
    !element.hasAttribute("disabled") &&
    element.getAttribute("aria-disabled") !== "true";

const useDetailReads = (groupId: number) => {
    const queryClient = useQueryClient();
    const [reads] = useState(() => {
        const queryHash = hashKey(organizationGroupQueryKeys.detail(groupId));
        let latest: DetailRead = { count: 0, group: undefined };
        return {
            subscribe: (onChange: () => void) =>
                queryClient.getQueryCache().subscribe((event) => {
                    if (
                        event.type !== "updated" ||
                        event.action.type !== "success" ||
                        event.action.manual ||
                        event.query.queryHash !== queryHash
                    ) {
                        return;
                    }
                    latest = {
                        count: latest.count + 1,
                        group: event.action.data as
                            | IOrganizationGroupDetail
                            | undefined,
                    };
                    onChange();
                }),
            latest: () => latest,
        };
    });
    return [
        useSyncExternalStore(reads.subscribe, reads.latest),
        reads.latest,
    ] as const;
};

export interface ExcludedSkillsSectionProps {
    group: IOrganizationGroupDetail;
}

export function ExcludedSkillsSection({ group }: ExcludedSkillsSectionProps) {
    const groupId = group.id;
    const excluded = confirmedExcludedSkills(group);
    const groupQuery = useOrganizationGroup(groupId);
    const queryClient = useQueryClient();
    const detailKey = organizationGroupQueryKeys.detail(groupId);
    const [detailRead, latestDetailRead] = useDetailReads(groupId);
    const candidates = useOrganizationGroupExcludableSkills(groupId);
    const excludeSkills = useExcludeOrganizationGroupSkills(groupId);
    const removeExclusion = useRemoveOrganizationGroupSkillExclusion(groupId);
    const retryRefresh =
        useRetryOrganizationGroupSkillExclusionRefresh(groupId);
    const pending = useIsOrganizationGroupSkillExclusionPending(groupId);
    const submission = useSingleFlight();
    const removal = useSingleFlight();
    const [draft, setDraft] = useState<IOrganizationGroupSkillMinimal[]>([]);
    const [exclusionError, setExclusionError] = useState<string | null>(null);
    const [unconfirmedExclusions, setUnconfirmedExclusions] = useState<
        IOrganizationGroupSkillMinimal[] | null
    >(null);
    const [removalAlert, setRemovalAlert] = useState<RemovalAlert | null>(null);
    const [focusRequest, setFocusRequest] = useState<FocusRequest | null>(null);
    const headingRef = useRef<HTMLHeadingElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const candidateRetryRef = useRef<HTMLButtonElement>(null);
    const retryRefreshRef = useRef<HTMLButtonElement>(null);
    const removeButtons = useRef(new Map<number, HTMLButtonElement>());
    const focusedRef = useRef<HTMLElement | null>(null);

    const notifyFailure = (message: string) =>
        toast.error(message, { description: group.name });

    useEffect(() => {
        if (!excluded) return;
        const excludedIds = new Set(excluded.map((skill) => skill.id));
        setDraft((current) =>
            current.some((skill) => excludedIds.has(skill.id))
                ? current.filter((skill) => !excludedIds.has(skill.id))
                : current
        );
    }, [excluded]);

    useEffect(() => {
        const confirmed = confirmedExcludedSkills(groupQuery.data);
        if (!unconfirmedExclusions || !groupQuery.isSuccess || !confirmed) {
            return;
        }
        const excludedIds = new Set(confirmed.map((skill) => skill.id));
        const remaining = unconfirmedExclusions.filter(
            (skill) => !excludedIds.has(skill.id)
        );
        if (remaining.length === unconfirmedExclusions.length) return;
        setUnconfirmedExclusions(remaining.length > 0 ? remaining : null);
        setExclusionError(
            remaining.length > 0 ? unconfirmedExclusionMessage(remaining) : null
        );
    }, [groupQuery.data, groupQuery.isSuccess, unconfirmedExclusions]);

    useEffect(() => {
        if (!removalAlert || detailRead.count <= removalAlert.readCount) return;
        const confirmed = confirmedExcludedSkills(detailRead.group);
        if (!confirmed) return;
        const stillExcluded = confirmed.some(
            (skill) => skill.id === removalAlert.skill.id
        );
        if (stillExcluded && removalAlert.kind !== "unconfirmed") return;
        setRemovalAlert(
            stillExcluded
                ? {
                      kind: "concurrent",
                      skill: removalAlert.skill,
                      readCount: detailRead.count,
                  }
                : null
        );
    }, [detailRead, removalAlert]);

    useEffect(() => {
        // Any render can unmount or disable the focused control, and the
        // combobox disables its input one render later.
        const frame = requestAnimationFrame(() => {
            const focused = focusedRef.current;
            const active = document.activeElement;
            if (
                !focused ||
                isFocusable(focused) ||
                (active !== null &&
                    active !== document.body &&
                    active !== focused)
            ) {
                return;
            }
            focusedRef.current = null;
            setFocusRequest((current) => current ?? { kind: "restore" });
        });
        return () => cancelAnimationFrame(frame);
    });

    useEffect(() => {
        if (!focusRequest || pending) return;
        const excludedIds = new Set((excluded ?? []).map((skill) => skill.id));
        if (
            (focusRequest.kind === "excluded" &&
                excluded &&
                !focusRequest.skillIds.every((id) => excludedIds.has(id))) ||
            (focusRequest.kind === "removed" &&
                excludedIds.has(focusRequest.skillId))
        ) {
            return;
        }
        // The combobox applies its disabled state one render after ours, so
        // targets are resolved once that render has reached the DOM.
        const frame = requestAnimationFrame(() => {
            setFocusRequest(null);
            const heading = headingRef.current;
            if (!heading) return;
            const section = heading.closest("section");
            const active = document.activeElement;
            const focusLost =
                active === null ||
                active === document.body ||
                (active instanceof HTMLElement &&
                    !!section?.contains(active) &&
                    !isFocusable(active));
            const focusInside = !focusLost && !!section?.contains(active);
            if (
                focusRequest.kind === "restore"
                    ? !focusLost
                    : !(focusLost || focusInside)
            ) {
                return;
            }
            const rows = excluded ?? [];
            const rowButton = (
                skill: IOrganizationGroupSkillMinimal | undefined
            ) => (skill ? removeButtons.current.get(skill.id) : undefined);
            const targets =
                focusRequest.kind === "excluded"
                    ? [inputRef.current]
                    : focusRequest.kind === "removed"
                      ? [
                            rowButton(rows[focusRequest.index]),
                            rowButton(rows[focusRequest.index - 1]),
                            inputRef.current,
                            candidateRetryRef.current,
                        ]
                      : [
                            retryRefreshRef.current,
                            rowButton(
                                rows.find(
                                    (skill) => skill.id === focusRequest.skillId
                                )
                            ),
                            inputRef.current,
                            candidateRetryRef.current,
                        ];
            (targets.find(isFocusable) ?? heading).focus();
        });
        return () => cancelAnimationFrame(frame);
    }, [excluded, focusRequest, pending]);

    const exclude = async () => {
        const submitted = draft;
        setExclusionError(null);
        setUnconfirmedExclusions(null);
        await submission.run(async () => {
            try {
                const saved = await excludeSkills.mutateAsync(
                    submitted.map((skill) => skill.id)
                );
                const confirmedIds = new Set(
                    (
                        confirmedExcludedSkills(
                            queryClient.getQueryData<IOrganizationGroupDetail>(
                                detailKey
                            )
                        ) ??
                        confirmedExcludedSkills(saved) ??
                        []
                    ).map((skill) => skill.id)
                );
                const unconfirmed = submitted.filter(
                    (skill) => !confirmedIds.has(skill.id)
                );
                setDraft((current) =>
                    current.filter((skill) => !confirmedIds.has(skill.id))
                );
                if (unconfirmed.length > 0) {
                    const message = unconfirmedExclusionMessage(unconfirmed);
                    setUnconfirmedExclusions(unconfirmed);
                    setExclusionError(message);
                    notifyFailure(message);
                    setFocusRequest({ kind: "restore" });
                } else {
                    setFocusRequest({
                        kind: "excluded",
                        skillIds: submitted.map((skill) => skill.id),
                    });
                }
            } catch (error) {
                let message =
                    axios.isAxiosError(error) && error.response?.status === 404
                        ? "This group no longer exists, so the selected skills were not excluded."
                        : organizationGroupErrorMessage(
                              error,
                              "Couldn't exclude the selected skills. Please try again."
                          );
                if (
                    error instanceof OrganizationGroupError &&
                    error.detail.code === "skill_not_excludable"
                ) {
                    const refusedIds = new Set(error.detail.skill_ids);
                    const refused = submitted.filter((skill) =>
                        refusedIds.has(skill.id)
                    );
                    if (refused.length > 0) {
                        message = refusalMessage(
                            refused,
                            submitted.length - refused.length
                        );
                    }
                    setDraft((current) =>
                        current.filter((skill) => !refusedIds.has(skill.id))
                    );
                }
                setExclusionError(message);
                notifyFailure(message);
                setFocusRequest({ kind: "restore" });
            }
        });
    };

    const settleRemoval = (
        outcome: OrganizationGroupSkillExclusionConfirmation,
        skill: IOrganizationGroupSkillMinimal,
        index: number
    ) => {
        if (outcome.kind === "present") {
            const alert: RemovalAlert = {
                kind: "concurrent",
                skill,
                readCount: latestDetailRead().count,
            };
            setRemovalAlert(alert);
            notifyFailure(removalAlertMessage(alert));
            setFocusRequest({ kind: "restore", skillId: skill.id });
            return;
        }
        setRemovalAlert(null);
        if (outcome.kind === "absent") {
            setFocusRequest({ kind: "removed", skillId: skill.id, index });
        }
    };

    const failRemoval = (
        error: unknown,
        skill: IOrganizationGroupSkillMinimal
    ) => {
        const readCount = latestDetailRead().count;
        const alert: RemovalAlert =
            error instanceof SkillExclusionRemovalUnconfirmedError
                ? { kind: "unconfirmed", skill, readCount }
                : {
                      kind: "failed",
                      skill,
                      readCount,
                      message: organizationGroupErrorMessage(
                          error,
                          "Couldn't remove the exclusion. Please try again."
                      ),
                  };
        setRemovalAlert(alert);
        notifyFailure(removalAlertMessage(alert));
        setFocusRequest({ kind: "restore", skillId: skill.id });
    };

    const remove = async (
        skill: IOrganizationGroupSkillMinimal,
        index: number
    ) => {
        setRemovalAlert(null);
        await removal.run(async () => {
            try {
                settleRemoval(
                    await removeExclusion.mutateAsync(skill.id),
                    skill,
                    index
                );
            } catch (error) {
                failRemoval(error, skill);
            }
        });
    };

    const retryRemovalRefresh = async () => {
        if (removalAlert?.kind !== "unconfirmed") return;
        const { skill } = removalAlert;
        const index = Math.max(
            0,
            (excluded ?? []).findIndex((row) => row.id === skill.id)
        );
        await removal.run(async () => {
            try {
                settleRemoval(
                    await retryRefresh.mutateAsync(skill.id),
                    skill,
                    index
                );
            } catch (error) {
                failRemoval(error, skill);
            }
        });
    };

    const rowIds = new Set((excluded ?? []).map((skill) => skill.id));
    const options = (candidates.data ?? []).filter(
        (skill) => !rowIds.has(skill.id)
    );
    const catalogEmpty =
        candidates.isSuccess &&
        candidates.data.length === 0 &&
        excluded?.length === 0 &&
        draft.length === 0;

    let picker: ReactNode;
    let pickerShowsError = false;
    if (!excluded) {
        pickerShowsError = true;
        picker = (
            <div className="flex flex-col gap-3">
                <ExcludedSkillsCombobox
                    options={[]}
                    value={draft}
                    onValueChange={setDraft}
                    onExclude={() => {}}
                    disabled={pending}
                    excluding={excludeSkills.isPending}
                    error={exclusionError}
                    disabledReason="Excluded global skills are temporarily unavailable. Retry to load them."
                    inputRef={inputRef}
                />
                <div>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={pending || groupQuery.isFetching}
                        aria-disabled={pending || groupQuery.isFetching}
                        onClick={() =>
                            removalAlert?.kind === "unconfirmed"
                                ? void retryRemovalRefresh()
                                : void groupQuery.refetch()
                        }
                    >
                        {groupQuery.isFetching ? "Retrying…" : "Retry"}
                    </Button>
                </div>
            </div>
        );
    } else if (candidates.isError && !candidates.data) {
        pickerShowsError = true;
        picker = (
            <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-2 rounded-md border border-dashed p-3 sm:flex-row sm:items-center sm:justify-between">
                    <p role="alert" className="text-destructive text-sm">
                        Couldn't load global skills.
                    </p>
                    <Button
                        ref={candidateRetryRef}
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        disabled={pending || candidates.isFetching}
                        aria-disabled={pending || candidates.isFetching}
                        onClick={() => void candidates.refetch()}
                    >
                        {candidates.isFetching ? "Retrying…" : "Retry"}
                    </Button>
                </div>
                <ExcludedSkillsCombobox
                    options={[]}
                    value={draft}
                    onValueChange={setDraft}
                    onExclude={() => void exclude()}
                    disabled={pending}
                    excluding={excludeSkills.isPending}
                    error={exclusionError}
                    disabledReason={
                        draft.length === 0
                            ? "Global skills couldn't be loaded. Retry to load them."
                            : null
                    }
                    inputRef={inputRef}
                />
            </div>
        );
    } else if (catalogEmpty) {
        picker = (
            <p className="text-muted-foreground text-sm">
                There are no global skills to exclude.
            </p>
        );
    } else {
        pickerShowsError = true;
        picker = (
            <div className="flex flex-col gap-3">
                <ExcludedSkillsCombobox
                    options={options}
                    value={draft}
                    onValueChange={setDraft}
                    onExclude={() => void exclude()}
                    loading={candidates.isPending}
                    disabled={pending}
                    excluding={excludeSkills.isPending}
                    error={exclusionError}
                    inputRef={inputRef}
                />
                {candidates.isError && (
                    <div className="flex flex-wrap items-center gap-2">
                        <p role="alert" className="text-destructive text-sm">
                            Couldn't refresh global skills. The list may be out
                            of date.
                        </p>
                        <Button
                            ref={candidateRetryRef}
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={pending || candidates.isFetching}
                            aria-disabled={pending || candidates.isFetching}
                            onClick={() => void candidates.refetch()}
                        >
                            {candidates.isFetching ? "Retrying…" : "Retry"}
                        </Button>
                    </div>
                )}
            </div>
        );
    }

    return (
        <section
            aria-label="Excluded global skills"
            data-testid="group-excluded-skills"
            className="bg-card rounded-lg border p-4"
            onFocus={(event) => {
                focusedRef.current = event.target;
            }}
        >
            <h2
                ref={headingRef}
                tabIndex={-1}
                className="text-foreground focus-visible:ring-ring/50 rounded-sm text-sm font-semibold outline-none focus-visible:ring-3"
            >
                Excluded global skills
            </h2>
            <p className="text-muted-foreground mt-0.5 text-xs">
                Global skills hidden from the owner and every member
                organization.
            </p>
            <div className="mt-3">
                {picker}
                {exclusionError && !pickerShowsError && (
                    <p role="alert" className="text-destructive mt-2 text-sm">
                        {exclusionError}
                    </p>
                )}
                {excluded && (
                    <div className="mt-4">
                        {excluded.length === 0 ? (
                            <p className="text-muted-foreground text-sm">
                                No global skills are excluded from this group.
                            </p>
                        ) : (
                            <ul className="divide-y rounded-md border">
                                {excluded.map((skill, index) => (
                                    <li
                                        key={skill.id}
                                        className="flex items-center justify-between gap-3 px-3 py-2"
                                    >
                                        <span className="min-w-0 text-sm break-words">
                                            {skill.name}
                                        </span>
                                        <Button
                                            ref={(
                                                element: HTMLButtonElement | null
                                            ) => {
                                                if (element) {
                                                    removeButtons.current.set(
                                                        skill.id,
                                                        element
                                                    );
                                                } else {
                                                    removeButtons.current.delete(
                                                        skill.id
                                                    );
                                                }
                                            }}
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="shrink-0"
                                            aria-label={`Remove ${skill.name}`}
                                            aria-disabled={pending}
                                            disabled={pending}
                                            onClick={() =>
                                                void remove(skill, index)
                                            }
                                        >
                                            Remove
                                        </Button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
                {removalAlert && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        <p role="alert" className="text-destructive text-sm">
                            {removalAlertMessage(removalAlert)}
                        </p>
                        {removalAlert.kind === "unconfirmed" && excluded && (
                            <Button
                                ref={retryRefreshRef}
                                type="button"
                                variant="outline"
                                size="sm"
                                aria-disabled={pending}
                                disabled={pending}
                                onClick={() => void retryRemovalRefresh()}
                            >
                                Retry refresh
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}
